import React, { useState, useEffect } from 'react';
import { XIcon, DollarSignIcon, CalendarIcon, MapPinIcon, UserIcon, PackageIcon, CreditCardIcon, LinkIcon, Trash2Icon, TagIcon, PlusIcon, CheckCircleIcon, TruckIcon, ClipboardCheckIcon } from 'lucide-react';
import { theme } from '../config/theme';
import { getPaymentStatus, createPaymentLink, getProduct, deletePaymentLink, validateCoupon, updateBooking, getCoupon, updatePaymentLinkStatus, getBooking, getAddons } from '../services/api';

interface BookingDetailModalProps {
  booking: any;
  onClose: () => void;
  onRefresh?: () => void;
  onReopen?: () => void;
}

const BookingDetailModal: React.FC<BookingDetailModalProps> = ({ booking, onClose, onRefresh, onReopen }) => {
  const [paymentStatus, setPaymentStatus] = useState<any>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [productName, setProductName] = useState<string>('');
  const [paymentLinkDescription, setPaymentLinkDescription] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string>('');
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [internalBooking, setInternalBooking] = useState<any>(booking);
  const [couponDetails, setCouponDetails] = useState<any>(null);
  const [availableAddons, setAvailableAddons] = useState<any[]>([]);
  const [showAddAddonForm, setShowAddAddonForm] = useState(false);
  const [selectedAddonId, setSelectedAddonId] = useState('');
  const [selectedAddonOption, setSelectedAddonOption] = useState('');
  const [addingAddon, setAddingAddon] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [editingDates, setEditingDates] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [updatingDates, setUpdatingDates] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [tempCustomerName, setTempCustomerName] = useState('');
  const [tempCustomerEmail, setTempCustomerEmail] = useState('');
  const [tempCustomerPhone, setTempCustomerPhone] = useState('');
  const [updatingCustomer, setUpdatingCustomer] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [tempAddressLine1, setTempAddressLine1] = useState('');
  const [tempAddressLine2, setTempAddressLine2] = useState('');
  const [tempAddressCity, setTempAddressCity] = useState('');
  const [tempAddressState, setTempAddressState] = useState('');
  const [tempAddressZip, setTempAddressZip] = useState('');
  const [updatingAddress, setUpdatingAddress] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setInternalBooking(booking);

      if (booking.product_id) {
        try {
          const product = await getProduct(booking.product_id);
          setProductName(product.name || 'Unknown Product');
        } catch (error) {
          console.error('Error fetching product:', error);
          setProductName('Unknown Product');
        }
      }

      if (booking.payment_intent_id) {
        setLoadingPayment(true);
        try {
          const status = await getPaymentStatus(booking.payment_intent_id);
          setPaymentStatus(status);
        } catch (error) {
          console.error('Error fetching payment status:', error);
        } finally {
          setLoadingPayment(false);
        }
      }

      // Check for payment link status updates and update paid_amount
      if (booking.booking_id && booking.payment_links && booking.payment_links.length > 0) {
        try {
          const updatedStatus = await updatePaymentLinkStatus(booking.booking_id);
          
          // Update internal booking with latest paid_amount
          if (updatedStatus.paid_amount !== undefined) {
            // Fetch fresh booking data from server to ensure we have the latest
            const freshBooking = await getBooking(booking.booking_id);
            
            setInternalBooking(freshBooking);
            
            // Trigger parent refresh if paid_amount changed
            if (updatedStatus.paid_amount !== booking.paid_amount && onRefresh) {
              await onRefresh();
            }
          }
        } catch (error) {
          console.error('Error updating payment link status:', error);
        }
      }

      // Fetch coupon details if coupon_id exists
      if (booking.coupon_id) {
        try {
          const coupon = await getCoupon(booking.coupon_id);
          setCouponDetails(coupon);
        } catch (error) {
          console.error('Error fetching coupon details:', error);
          setCouponDetails(null);
        }
      }

      // Fetch available addons
      try {
        const addons = await getAddons();
        setAvailableAddons(addons);
      } catch (error) {
        console.error('Error fetching addons:', error);
        setAvailableAddons([]);
      }
    };

    fetchData();
  }, [booking.product_id, booking.payment_intent_id, booking.coupon_id, booking.booking_id]);

  useEffect(() => {
    setInternalBooking(booking);
  }, [booking]);

  useEffect(() => {
    setPaymentLinkDescription(prev => prev || '');
  }, [internalBooking.total_price, internalBooking.discount_amount, internalBooking.discount_type, internalBooking.discount_value]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC'
    });
  };

  const formatDateOnly = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  const formatDateEST = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    });
  };

  const getPaymentStatusDisplay = () => {
    if (loadingPayment) return 'Loading...';

    // All monetary values are stored in cents, convert to dollars
    const totalPrice = parseFloat(internalBooking.total_price || 0) / 100;
    const totalPaid = parseFloat(internalBooking.paid_amount || 0) / 100;

    if (totalPaid >= totalPrice) {
      return 'Paid in Full';
    } else if (totalPaid > 0) {
      return 'Partially Paid';
    } else {
      return 'Not Paid';
    }
  };

  const getRemainingBalance = () => {
    // All monetary values are stored in cents, convert to dollars
    const totalPrice = parseFloat(internalBooking.total_price || 0) / 100;
    const amountPaid = parseFloat(internalBooking.paid_amount || 0) / 100;
    
    return Math.max(0, totalPrice - amountPaid);
  };

  const handleCreatePaymentLink = async () => {
    const remainingBalance = getRemainingBalance();
    if (remainingBalance <= 0) {
      alert('No remaining balance to pay');
      return;
    }

    if (!paymentLinkDescription.trim()) {
      alert('Please enter a payment description');
      return;
    }

    setCreatingLink(true);
    try {
      const response = await createPaymentLink(
        internalBooking.booking_id,
        Math.round(remainingBalance * 100), // Convert to cents
        paymentLinkDescription
      );
      setPaymentLink(response.payment_link);
      
      // Update internal booking to include the new payment link
      setInternalBooking((prev: any) => ({
        ...prev,
        payment_links: [...(prev.payment_links || []), {
          payment_link_id: response.payment_link_id,
          payment_link_url: response.payment_link,
          amount: Math.round(remainingBalance * 100), // Store in cents
          description: paymentLinkDescription,
          status: 'pending',
          created_at: new Date().toISOString()
        }]
      }));
      
      if (onRefresh) onRefresh();
    } catch (error: any) {
      console.error('Error creating payment link:', error);
      alert(error.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setCreatingLink(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Payment link copied to clipboard!');
  };

  const handleApplyDiscount = async () => {
    if (!discountValue.trim() || parseFloat(discountValue) <= 0) {
      setDiscountError('Please enter a valid discount amount');
      return;
    }

    setApplyingDiscount(true);
    setDiscountError('');

    try {
      // total_price is stored in cents, convert to dollars for calculation
      const totalPrice = parseFloat(internalBooking.total_price || 0) / 100;
      const taxRate = 0.08;
      
      const subtotal = totalPrice / (1 + taxRate);
      
      let discountAmount = 0;
      if (discountType === 'percentage') {
        const percentage = parseFloat(discountValue);
        if (percentage > 100) {
          setDiscountError('Percentage cannot exceed 100%');
          setApplyingDiscount(false);
          return;
        }
        discountAmount = subtotal * (percentage / 100);
      } else {
        discountAmount = parseFloat(discountValue);
        if (discountAmount > subtotal) {
          setDiscountError('Discount cannot exceed subtotal');
          setApplyingDiscount(false);
          return;
        }
      }
      
      const newSubtotal = subtotal - discountAmount;
      const newTotalPrice = newSubtotal * (1 + taxRate);
      
      await updateBooking(internalBooking.booking_id, {
        discount_amount: Math.round(discountAmount * 100), // Convert to cents
        discount_type: discountType,
        discount_value: parseFloat(discountValue),
        total_price: Math.round(newTotalPrice * 100) // Convert to cents
      });

      setDiscountValue('');
      if (onRefresh) {
        onRefresh();
      }
      
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Discount applied successfully! Saved $${discountAmount.toFixed(2)}
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);
      
      const updatedBooking = {
        ...internalBooking,
        discount_amount: Math.round(discountAmount * 100), // Store in cents
        discount_type: discountType,
        discount_value: parseFloat(discountValue),
        total_price: Math.round(newTotalPrice * 100) // Store in cents
      };
      
      setInternalBooking(updatedBooking);
      
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 100);
      }
    } catch (error: any) {
      setDiscountError(error.response?.data?.detail || 'Failed to apply discount');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!window.confirm('Are you sure you want to remove this discount?')) {
      return;
    }

    try {
      // All values stored in cents, convert to dollars for calculation
      const currentTotal = parseFloat(internalBooking.total_price || 0) / 100;
      const discountAmount = parseFloat(internalBooking.discount_amount || 0) / 100;
      const taxRate = 0.08;
      
      const currentSubtotal = currentTotal / (1 + taxRate);
      const originalSubtotal = currentSubtotal + discountAmount;
      const originalTotal = originalSubtotal * (1 + taxRate);

      await updateBooking(internalBooking.booking_id, {
        discount_amount: 0,
        discount_type: null,
        discount_value: null,
        coupon_id: null,
        coupon_code: null,
        total_price: Math.round(originalTotal * 100) // Convert to cents
      });

      if (onRefresh) {
        onRefresh();
      }
      
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Discount removed successfully
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);
      
      const updatedBooking = {
        ...internalBooking,
        discount_amount: 0,
        discount_type: null,
        discount_value: null,
        total_price: Math.round(originalTotal * 100) // Store in cents
      };
      
      setInternalBooking(updatedBooking);
      
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 200);
      }
    } catch (error: any) {
      console.error('Error removing discount:', error);
      alert('Failed to remove discount. Please try again.');
    }
  };

  const handleAddAddon = async () => {
    if (!selectedAddonId) {
      alert('Please select an addon');
      return;
    }

    setAddingAddon(true);
    try {
      const selectedAddon = availableAddons.find(a => a.addon_id === selectedAddonId);
      if (!selectedAddon) {
        alert('Selected addon not found');
        return;
      }

      const currentAddons = internalBooking.addons || [];
      // Don't send price field - backend will fetch it from addon master data
      const newAddon = {
        addon_id: selectedAddon.addon_id,
        quantity: 1,
        selected_option: selectedAddonOption || null
      };

      const updatedAddons = [...currentAddons, newAddon];
      const currentTotal = parseFloat(internalBooking.total_price || 0) / 100;
      const addonPrice = parseFloat(String(selectedAddon.price)) / 100;
      const newTotal = currentTotal + addonPrice;

      await updateBooking(internalBooking.booking_id, {
        addons: updatedAddons,
        total_price: Math.round(newTotal * 100)
      });

      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Add-on added successfully!
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);

      // Refresh booking details to get updated addon info from backend
      const freshBooking = await getBooking(internalBooking.booking_id);
      setInternalBooking(freshBooking);

      setSelectedAddonId('');
      setSelectedAddonOption('');
      setShowAddAddonForm(false);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('Error adding addon:', error);
      alert('Failed to add addon. Please try again.');
    } finally {
      setAddingAddon(false);
    }
  };

  const handleRemoveAddon = async (index: number) => {
    if (!window.confirm('Are you sure you want to remove this add-on?')) {
      return;
    }

    try {
      const currentAddons = internalBooking.addons || [];
      const addonToRemove = currentAddons[index];
      
      if (!addonToRemove) {
        alert('Addon not found');
        return;
      }
      
      // Remove addon from array
      const updatedAddons = currentAddons.filter((_: any, i: number) => i !== index);
      
      // Calculate new total price - handle both price formats
      const currentTotal = parseFloat(internalBooking.total_price || 0) / 100;
      let addonPriceCents = 0;
      
      // Handle different price structures
      if (addonToRemove.price && typeof addonToRemove.price === 'object' && addonToRemove.price.amount_cents) {
        addonPriceCents = addonToRemove.price.amount_cents;
      } else if (typeof addonToRemove.price === 'number') {
        addonPriceCents = addonToRemove.price;
      }
      
      const addonPrice = addonPriceCents / 100;
      const newTotal = Math.max(0, currentTotal - addonPrice); // Ensure total doesn't go negative

      await updateBooking(internalBooking.booking_id, {
        addons: updatedAddons,
        total_price: Math.round(newTotal * 100)
      });

      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Add-on removed successfully!
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);

      // Refresh booking details to get updated info from backend
      const freshBooking = await getBooking(internalBooking.booking_id);
      setInternalBooking(freshBooking);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('Error removing addon:', error);
      alert('Failed to remove addon. Please try again.');
    }
  };

  const handleMarkAsPaid = async () => {
    if (!window.confirm('Mark this booking as fully paid? This will set the paid amount to the total price.')) {
      return;
    }

    setMarkingPaid(true);
    try {
      const totalPrice = parseFloat(internalBooking.total_price || 0);
      
      await updateBooking(internalBooking.booking_id, {
        paid_amount: totalPrice // Already in cents
      });

      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Booking marked as paid!
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);

      // Refresh booking details
      const freshBooking = await getBooking(internalBooking.booking_id);
      setInternalBooking(freshBooking);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('Error marking as paid:', error);
      alert('Failed to mark as paid. Please try again.');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleCustomerChange = async () => {
    if (!tempCustomerName || !tempCustomerEmail) {
      alert('Please provide both name and email');
      return;
    }

    setUpdatingCustomer(true);
    try {
      const payload = {
        customer: {
          full_name: tempCustomerName,
          email: tempCustomerEmail,
          phone: tempCustomerPhone
        },
        customer_name: tempCustomerName,
        customer_email: tempCustomerEmail,
        customer_phone: tempCustomerPhone
      };
      console.log('Updating customer with payload:', payload);
      await updateBooking(internalBooking.booking_id, payload);

      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Customer information updated successfully!
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);

      const freshBooking = await getBooking(internalBooking.booking_id);
      setInternalBooking(freshBooking);
      setEditingCustomer(false);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('Error updating customer info:', error);
      alert('Failed to update customer information. Please try again.');
    } finally {
      setUpdatingCustomer(false);
    }
  };

  const handleAddressChange = async () => {
    if (!tempAddressLine1 || !tempAddressCity || !tempAddressState || !tempAddressZip) {
      alert('Please provide address line 1, city, state, and zip code');
      return;
    }

    setUpdatingAddress(true);
    try {
      const payload = {
        delivery_address: {
          line1: tempAddressLine1,
          line2: tempAddressLine2,
          city: tempAddressCity,
          state: tempAddressState,
          postal_code: tempAddressZip
        }
      };
      console.log('Updating address with payload:', payload);
      await updateBooking(internalBooking.booking_id, payload);

      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Delivery address updated successfully!
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);

      const freshBooking = await getBooking(internalBooking.booking_id);
      setInternalBooking(freshBooking);
      setEditingAddress(false);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('Error updating address:', error);
      alert('Failed to update delivery address. Please try again.');
    } finally {
      setUpdatingAddress(false);
    }
  };

  const handleDateChange = async () => {
    console.log('handleDateChange called with:', { tempStartDate, tempEndDate });
    if (!tempStartDate || !tempEndDate) {
      alert('Please select both start and end dates');
      return;
    }

    // Validate that end date is not before start date (same day is allowed)
    if (new Date(tempEndDate) < new Date(tempStartDate)) {
      alert('End date cannot be before start date');
      return;
    }

    setUpdatingDates(true);
    try {
      // Convert dates to UTC ISO format for backend
      const startDateUTC = new Date(tempStartDate + 'T12:00:00').toISOString();
      const endDateUTC = new Date(tempEndDate + 'T14:00:00').toISOString();

      const payload = {
        start_date: startDateUTC,
        end_date: endDateUTC,
        start_time_utc: startDateUTC,
        end_time_utc: endDateUTC
      };
      console.log('Updating dates with payload:', payload);
      await updateBooking(internalBooking.booking_id, payload);

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Booking dates updated successfully!
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);

      // Refresh booking details
      const freshBooking = await getBooking(internalBooking.booking_id);
      setInternalBooking(freshBooking);
      setEditingDates(false);
      setTempStartDate('');
      setTempEndDate('');

      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('Error updating dates:', error);
      alert('Failed to update dates. Please try again.');
    } finally {
      setUpdatingDates(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    const statusMessages: { [key: string]: string } = {
      'dropped_off': 'Mark this booking as dropped off?',
      'completed': 'Mark this booking as completed?',
      'confirmed': 'Mark this booking as confirmed?'
    };

    if (!window.confirm(statusMessages[newStatus] || `Change status to ${newStatus}?`)) {
      return;
    }

    setUpdatingStatus(true);
    try {
      await updateBooking(internalBooking.booking_id, {
        status: newStatus.toUpperCase()
      });

      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Status updated to ${newStatus}!
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);

      // Refresh booking details
      const freshBooking = await getBooking(internalBooking.booking_id);
      setInternalBooking(freshBooking);

      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeletePaymentLink = async (paymentLinkId: string) => {
    if (!window.confirm('Are you sure you want to delete this payment link?')) {
      return;
    }

    setDeletingLinkId(paymentLinkId);
    try {
      await deletePaymentLink(paymentLinkId);
      
      setInternalBooking((prev: any) => ({
        ...prev,
        payment_links: (prev.payment_links || []).filter((link: any) => link.payment_link_id !== paymentLinkId)
      }));
      
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      successMessage.innerHTML = `
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Payment link deleted successfully
      `;
      document.body.appendChild(successMessage);
      setTimeout(() => successMessage.remove(), 3000);
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting payment link:', error);
      alert('Failed to delete payment link. Please try again.');
    } finally {
      setDeletingLinkId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Booking Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2" style={{ color: theme.colors.primary[600] }} />
                  Booking Information
                </h3>
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Booking ID:</span>
                    <p className="text-sm text-gray-900 font-mono">{internalBooking.booking_id}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Product:</span>
                    <p className="text-sm text-gray-900">{productName}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Status:</span>
                    <span className={`inline-flex ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                      internalBooking.status === 'CONFIRMED' || internalBooking.status === 'confirmed'
                        ? 'bg-green-100 text-green-800'
                        : internalBooking.status === 'DROPPED_OFF' || internalBooking.status === 'dropped_off'
                        ? 'bg-blue-100 text-blue-800'
                        : internalBooking.status === 'COMPLETED' || internalBooking.status === 'completed'
                        ? 'bg-purple-100 text-purple-800'
                        : internalBooking.status === 'PENDING' || internalBooking.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {internalBooking.status || 'unknown'}
                    </span>
                  </div>
                  
                  {/* Status Workflow Buttons */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <span className="text-sm font-medium text-gray-600 block mb-2">Update Status:</span>
                    <div className="flex flex-wrap gap-2">
                      {(internalBooking.status === 'CONFIRMED' || internalBooking.status === 'confirmed') && (
                        <button
                          onClick={() => handleStatusChange('dropped_off')}
                          disabled={updatingStatus}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                          <TruckIcon className="h-3.5 w-3.5 mr-1" />
                          Mark as Dropped Off
                        </button>
                      )}
                      {(internalBooking.status === 'DROPPED_OFF' || internalBooking.status === 'dropped_off') && (
                        <button
                          onClick={() => handleStatusChange('completed')}
                          disabled={updatingStatus}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                          <ClipboardCheckIcon className="h-3.5 w-3.5 mr-1" />
                          Mark as Completed
                        </button>
                      )}
                      {(internalBooking.status === 'COMPLETED' || internalBooking.status === 'completed' || internalBooking.status === 'DROPPED_OFF' || internalBooking.status === 'dropped_off') && (
                        <button
                          onClick={() => handleStatusChange('confirmed')}
                          disabled={updatingStatus}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        >
                          Reset to Confirmed
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-600">Booking Dates:</span>
                      {!editingDates && (
                        <button
                          onClick={() => {
                            setEditingDates(true);
                            // Set initial values from current booking
                            const startDate = new Date(internalBooking.start_time_utc);
                            const endDate = new Date(internalBooking.end_time_utc);
                            setTempStartDate(startDate.toISOString().split('T')[0]);
                            setTempEndDate(endDate.toISOString().split('T')[0]);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Edit Dates
                        </button>
                      )}
                    </div>
                    {editingDates ? (
                      <div className="space-y-2 bg-white p-3 rounded border border-gray-300">
                        <div>
                          <label className="text-xs font-medium text-gray-600 block mb-1">Dropoff Date:</label>
                          <input
                            type="date"
                            value={tempStartDate}
                            onChange={(e) => setTempStartDate(e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 block mb-1">Pickup Date:</label>
                          <input
                            type="date"
                            value={tempEndDate}
                            onChange={(e) => setTempEndDate(e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={handleDateChange}
                            disabled={updatingDates}
                            className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {updatingDates ? 'Saving...' : 'Save Dates'}
                          </button>
                          <button
                            onClick={() => {
                              setEditingDates(false);
                              setTempStartDate('');
                              setTempEndDate('');
                            }}
                            disabled={updatingDates}
                            className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div>
                          <span className="text-xs text-gray-500">Dropoff:</span>
                          <p className="text-sm text-gray-900">{formatDateOnly(internalBooking.start_time_utc)}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Pickup:</span>
                          <p className="text-sm text-gray-900">{formatDateOnly(internalBooking.end_time_utc)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Created:</span>
                    <p className="text-sm text-gray-900">{formatDateEST(internalBooking.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Reserved Dates Error Warning */}
              {internalBooking.reserved_dates_error && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <XIcon className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Calendar Update Required
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p className="font-medium">Failed to mark reserved dates in calendar:</p>
                        <p className="mt-1 text-xs bg-red-100 p-2 rounded font-mono">
                          {internalBooking.reserved_dates_error.error}
                        </p>
                        <div className="mt-2 text-xs">
                          <p className="font-medium">Manual Action Required:</p>
                          <p>Please manually mark the following dates as reserved in the reserved dates table:</p>
                          <div className="mt-1 bg-white p-2 rounded border border-red-300">
                            <p><strong>Product ID:</strong> {internalBooking.reserved_dates_error.product_id}</p>
                            <p><strong>Date Range:</strong> {internalBooking.reserved_dates_error.start_date} to {internalBooking.reserved_dates_error.end_date}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2" style={{ color: theme.colors.primary[600] }} />
                    Customer Information
                  </h3>
                  {!editingCustomer && (
                    <button
                      onClick={() => {
                        setEditingCustomer(true);
                        setTempCustomerName(internalBooking.customer?.full_name || '');
                        setTempCustomerEmail(internalBooking.customer?.email || '');
                        setTempCustomerPhone(internalBooking.customer?.phone || '');
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Edit Info
                    </button>
                  )}
                </div>
                {editingCustomer ? (
                  <div className="space-y-3 bg-white p-3 rounded border border-gray-300">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Name:</label>
                      <input
                        type="text"
                        value={tempCustomerName}
                        onChange={(e) => setTempCustomerName(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Email:</label>
                      <input
                        type="email"
                        value={tempCustomerEmail}
                        onChange={(e) => setTempCustomerEmail(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Phone:</label>
                      <input
                        type="tel"
                        value={tempCustomerPhone}
                        onChange={(e) => setTempCustomerPhone(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={handleCustomerChange}
                        disabled={updatingCustomer}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {updatingCustomer ? 'Saving...' : 'Save Info'}
                      </button>
                      <button
                        onClick={() => setEditingCustomer(false)}
                        disabled={updatingCustomer}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Name:</span>
                      <p className="text-sm text-gray-900">{internalBooking.customer?.full_name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Email:</span>
                      <p className="text-sm text-gray-900">{internalBooking.customer?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Phone:</span>
                      <p className="text-sm text-gray-900">{internalBooking.customer?.phone || 'N/A'}</p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <MapPinIcon className="h-5 w-5 mr-2" style={{ color: theme.colors.primary[600] }} />
                    Delivery Address
                  </h3>
                  {!editingAddress && (
                    <button
                      onClick={() => {
                        setEditingAddress(true);
                        setTempAddressLine1(internalBooking.delivery_address?.line1 || '');
                        setTempAddressLine2(internalBooking.delivery_address?.line2 || '');
                        setTempAddressCity(internalBooking.delivery_address?.city || '');
                        setTempAddressState(internalBooking.delivery_address?.state || '');
                        setTempAddressZip(internalBooking.delivery_address?.postal_code || '');
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Edit Address
                    </button>
                  )}
                </div>
                {editingAddress ? (
                  <div className="space-y-3 bg-white p-3 rounded border border-gray-300">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Address Line 1:</label>
                      <input
                        type="text"
                        value={tempAddressLine1}
                        onChange={(e) => setTempAddressLine1(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Address Line 2 (Optional):</label>
                      <input
                        type="text"
                        value={tempAddressLine2}
                        onChange={(e) => setTempAddressLine2(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">City:</label>
                        <input
                          type="text"
                          value={tempAddressCity}
                          onChange={(e) => setTempAddressCity(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">State:</label>
                        <input
                          type="text"
                          value={tempAddressState}
                          onChange={(e) => setTempAddressState(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          maxLength={2}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Zip Code:</label>
                      <input
                        type="text"
                        value={tempAddressZip}
                        onChange={(e) => setTempAddressZip(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={handleAddressChange}
                        disabled={updatingAddress}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {updatingAddress ? 'Saving...' : 'Save Address'}
                      </button>
                      <button
                        onClick={() => setEditingAddress(false)}
                        disabled={updatingAddress}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {internalBooking.delivery_address ? (
                      <div className="text-sm text-gray-900">
                        <p>{internalBooking.delivery_address.line1}</p>
                        {internalBooking.delivery_address.line2 && <p>{internalBooking.delivery_address.line2}</p>}
                        <p>{internalBooking.delivery_address.city}, {internalBooking.delivery_address.state} {internalBooking.delivery_address.postal_code}</p>
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              `${internalBooking.delivery_address.line1}${internalBooking.delivery_address.line2 ? ' ' + internalBooking.delivery_address.line2 : ''}, ${internalBooking.delivery_address.city}, ${internalBooking.delivery_address.state} ${internalBooking.delivery_address.postal_code}`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            <MapPinIcon className="h-4 w-4 mr-1" />
                            Open in Google Maps
                          </a>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No address provided</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <DollarSignIcon className="h-5 w-5 mr-2" style={{ color: theme.colors.primary[600] }} />
                  Payment Information
                </h3>
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  {(() => {
                    const parseCents = (val: any) => {
                      if (typeof val === 'string') return parseFloat(val) / 100;
                      if (typeof val === 'number') return val / 100;
                      if (val && typeof val === 'object' && val.N) return parseFloat(val.N) / 100;
                      return 0;
                    };

                    const totalPrice = parseCents(internalBooking.total_price);
                    const discountValue = parseCents(internalBooking.discount_amount);

                    // total_price already has the discount deducted, so the
                    // pre-discount price is total + discount. Show before/discount/due
                    // in order to make it clear the total is the amount after discount.
                    if (discountValue > 0) {
                      const priceBeforeDiscount = totalPrice + discountValue;
                      return (
                        <>
                          <div>
                            <span className="text-sm font-medium text-gray-600">Price Before Discount:</span>
                            <p className="text-sm text-gray-900">${priceBeforeDiscount.toFixed(2)}</p>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-600">Discount:</span>
                            <div className="mt-1">
                              <p className="text-sm font-semibold text-green-600">
                                -${discountValue.toFixed(2)}
                                {internalBooking.discount_type === 'percentage'
                                  ? ` (${parseFloat(internalBooking.discount_value || 0).toFixed(0)}% off)`
                                  : internalBooking.discount_type === 'fixed'
                                  ? ` (Fixed discount)`
                                  : ''}
                              </p>
                              {(internalBooking.coupon_code || couponDetails?.code) && (
                                <div className="text-xs text-gray-500 mt-1">
                                  <p>Promo Code: {internalBooking.coupon_code || couponDetails?.code}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-600">Total Price:</span>
                            <p className="text-lg font-bold text-gray-900">${totalPrice.toFixed(2)}</p>
                          </div>
                        </>
                      );
                    }

                    return (
                      <div>
                        <span className="text-sm font-medium text-gray-600">Total Price:</span>
                        <p className="text-lg font-bold text-gray-900">${totalPrice.toFixed(2)}</p>
                      </div>
                    );
                  })()}
                  {(() => {
                    const parseCents = (val: any) => {
                      if (typeof val === 'number') return val / 100;
                      if (typeof val === 'string') return parseFloat(val) / 100;
                      if (val && typeof val === 'object' && val.N) return parseFloat(val.N) / 100;
                      return 0;
                    };
                    const deliveryFee = parseCents(internalBooking.delivery_fee);
                    if (deliveryFee <= 0) return null;
                    const miles = parseFloat(String(internalBooking.delivery_distance_miles || 0));
                    return (
                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          Delivery Fee{miles > 0 ? ` (${Math.round(miles)} mi)` : ''}:
                        </span>
                        <p className="text-sm text-gray-900">${deliveryFee.toFixed(2)}</p>
                      </div>
                    );
                  })()}
                  <div>
                    <span className="text-sm font-medium text-gray-600">Total Paid:</span>
                    <p className="text-lg font-semibold text-green-700">${(() => {
                      // Use the paid_amount field from the booking (already includes all payments)
                      let totalPaid = parseFloat(internalBooking.paid_amount || 0) / 100;
                      return totalPaid.toFixed(2);
                    })()}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Remaining Balance:</span>
                    <p className="text-lg font-semibold" style={{ color: theme.colors.primary[700] }}>
                      ${getRemainingBalance().toFixed(2)}
                    </p>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-sm font-medium text-gray-600">Payment Status:</span>
                    <p className="text-sm font-semibold text-gray-900">{getPaymentStatusDisplay()}</p>
                  </div>
                  
                  {/* Mark as Paid Button */}
                  {getRemainingBalance() > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <button
                        onClick={handleMarkAsPaid}
                        disabled={markingPaid}
                        className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        {markingPaid ? 'Marking as Paid...' : 'Mark as Paid'}
                      </button>
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        This will mark the booking as fully paid
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Stripe Payment Details */}
              {(internalBooking.payment_intent_id || (internalBooking.payment_links && internalBooking.payment_links.some((link: any) => link.payment_intent_id))) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <CreditCardIcon className="h-5 w-5 mr-2" style={{ color: theme.colors.primary[600] }} />
                    Stripe Payment Details
                  </h3>
                  <div className="space-y-2 bg-gray-50 p-4 rounded-lg">
                    {internalBooking.payment_intent_id && (
                      <div className="pb-2 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">Initial Deposit Payment:</span>
                        <div className="mt-1">
                          <a
                            href={`https://dashboard.stripe.com/payments/${internalBooking.payment_intent_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-mono break-all flex items-center"
                          >
                            {internalBooking.payment_intent_id}
                            <svg className="w-3 h-3 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                          <p className="text-xs text-gray-500 mt-1">
                            Amount: ${internalBooking.deposit_amount ? (parseFloat(String(internalBooking.deposit_amount)) / 100).toFixed(2) : '0.00'}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {internalBooking.payment_links && internalBooking.payment_links
                      .filter((link: any) => link.payment_intent_id && link.status === 'paid')
                      .map((link: any, index: number) => (
                        <div key={index} className={index > 0 || internalBooking.payment_intent_id ? "pt-2" : ""}>
                          <span className="text-sm font-medium text-gray-600">Payment Link Transaction #{index + 1}:</span>
                          <div className="mt-1">
                            <a
                              href={`https://dashboard.stripe.com/payments/${link.payment_intent_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-mono break-all flex items-center"
                            >
                              {link.payment_intent_id}
                              <svg className="w-3 h-3 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                            <p className="text-xs text-gray-500 mt-1">
                              Amount: ${(parseFloat(link.amount || 0) / 100).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    
                    <div className="pt-2 mt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 italic">
                        Click any payment ID to view the transaction in your Stripe dashboard
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {getRemainingBalance() > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <TagIcon className="h-5 w-5 mr-2" style={{ color: theme.colors.primary[600] }} />
                  Apply Discount
                </h3>
                {internalBooking.discount_amount && parseFloat(internalBooking.discount_amount) > 0 ? (
                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-green-800 mb-1">Applied Discount</p>
                        <p className="text-sm font-semibold text-green-600">
                              -${((parseFloat(internalBooking.discount_amount || 0)) / 100).toFixed(2)}
                              {couponDetails 
                                ? (couponDetails.discount_type === 'percentage' 
                                    ? ` (${parseFloat(couponDetails.discount_value || 0).toFixed(0)}% off)` 
                                    : ` ($${(parseFloat(couponDetails.discount_value || 0)).toFixed(2)} off)`)
                                : (internalBooking.discount_type === 'percentage' 
                                    ? ` (${parseFloat(internalBooking.discount_value || 0).toFixed(0)}% off)` 
                                    : ` ($${(parseFloat(internalBooking.discount_value || 0)).toFixed(2)} off)`)
                              }
                            </p>
                            {couponDetails && (
                              <div className="text-xs text-gray-500 mt-1">
                                <p>Promo Code: {couponDetails.code}</p>
                              </div>
                            )}
                      </div>
                      <button
                        onClick={handleRemoveDiscount}
                        className="text-red-600 hover:text-red-800"
                        title="Remove discount"
                      >
                        <Trash2Icon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Type
                      </label>
                      <div className="flex gap-4 mb-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="percentage"
                            checked={discountType === 'percentage'}
                            onChange={(e) => setDiscountType(e.target.value as 'percentage')}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Percentage (%)</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="fixed"
                            checked={discountType === 'fixed'}
                            onChange={(e) => setDiscountType(e.target.value as 'fixed')}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700">Fixed Amount ($)</span>
                        </label>
                      </div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          {discountType === 'fixed' && (
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          )}
                          <input
                            type="number"
                            value={discountValue}
                            onChange={(e) => {
                              setDiscountValue(e.target.value);
                              setDiscountError('');
                            }}
                            placeholder={discountType === 'percentage' ? '10' : '50.00'}
                            step={discountType === 'percentage' ? '1' : '0.01'}
                            min="0"
                            max={discountType === 'percentage' ? '100' : undefined}
                            className={`w-full ${discountType === 'fixed' ? 'pl-7' : ''} px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2`}
                            style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
                          />
                          {discountType === 'percentage' && (
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                          )}
                        </div>
                        <button
                          onClick={handleApplyDiscount}
                          disabled={applyingDiscount || !discountValue.trim()}
                          className="px-4 py-2 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          style={{ backgroundColor: theme.colors.primary[600] }}
                        >
                          {applyingDiscount ? 'Applying...' : (
                            <>
                              <PlusIcon className="h-4 w-4 mr-1" />
                              Apply
                            </>
                          )}
                        </button>
                      </div>
                      {discountError && (
                        <p className="mt-2 text-sm text-red-600">{discountError}</p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        Note: Discount is applied to subtotal (before tax)
                      </p>
                    </div>
                  </div>
                )}
              </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <PackageIcon className="h-5 w-5 mr-2" style={{ color: theme.colors.primary[600] }} />
                  Add-ons
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  {internalBooking.addons && internalBooking.addons.length > 0 ? (
                    <div className="space-y-2">
                      {internalBooking.addons.map((addon: any, index: number) => (
                        <div key={index} className="flex justify-between items-center p-2 hover:bg-gray-100 rounded">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{addon.name}</p>
                            {addon.selected_option && (
                              <p className="text-xs text-gray-500">
                                Option: {typeof addon.selected_option === 'string' 
                                  ? addon.selected_option 
                                  : addon.selected_option?.label || 'Selected'}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <p className="text-sm font-semibold text-gray-900">
                              ${(() => {
                                if (addon.price && typeof addon.price === 'object' && addon.price.amount_cents) {
                                  return (addon.price.amount_cents / 100).toFixed(2);
                                } else if (addon.price && typeof addon.price === 'number') {
                                  return (addon.price / 100).toFixed(2);
                                } else if (addon.price && typeof addon.price === 'string') {
                                  return (parseFloat(addon.price) / 100).toFixed(2);
                                }
                                return '0.00';
                              })()}
                            </p>
                            <button
                              onClick={() => handleRemoveAddon(index)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove add-on"
                            >
                              <Trash2Icon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No add-ons selected</p>
                  )}
                  
                  {!showAddAddonForm ? (
                    <button
                      onClick={() => setShowAddAddonForm(true)}
                      className="w-full mt-3 px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-gray-400 hover:text-gray-700 flex items-center justify-center"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add Add-on
                    </button>
                  ) : (
                    <div className="mt-3 p-3 border border-gray-300 rounded-md space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Add-on
                        </label>
                        <select
                          value={selectedAddonId}
                          onChange={(e) => {
                            setSelectedAddonId(e.target.value);
                            setSelectedAddonOption('');
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                          style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
                        >
                          <option value="">Choose an add-on...</option>
                          {availableAddons.map(addon => (
                            <option key={addon.addon_id} value={addon.addon_id}>
                              {addon.name} - ${(parseFloat(String(addon.price)) / 100).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {selectedAddonId && (() => {
                        const selectedAddon = availableAddons.find(a => a.addon_id === selectedAddonId);
                        return selectedAddon?.options && selectedAddon.options.length > 0 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {selectedAddon.option_label || 'Select Option'}
                            </label>
                            <select
                              value={selectedAddonOption}
                              onChange={(e) => setSelectedAddonOption(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                              style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
                            >
                              <option value="">Choose an option...</option>
                              {selectedAddon.options.map((opt: any, idx: number) => (
                                <option key={idx} value={opt.label}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })()}
                      
                      <div className="flex gap-2">
                        <button
                          onClick={handleAddAddon}
                          disabled={!selectedAddonId || addingAddon}
                          className="flex-1 px-4 py-2 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: theme.colors.primary[600] }}
                        >
                          {addingAddon ? 'Adding...' : 'Add'}
                        </button>
                        <button
                          onClick={() => {
                            setShowAddAddonForm(false);
                            setSelectedAddonId('');
                            setSelectedAddonOption('');
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {internalBooking.notes && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{internalBooking.notes}</p>
                  </div>
                </div>
              )}

              {internalBooking.payment_links && internalBooking.payment_links.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <LinkIcon className="h-5 w-5 mr-2" style={{ color: theme.colors.primary[600] }} />
                    Payment Links History
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    {internalBooking.payment_links.map((link: any, index: number) => (
                      <div key={index} className="bg-white p-3 rounded border border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{link.description}</p>
                            <p className="text-xs text-gray-500">
                              Created: {new Date(link.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">${(parseFloat(link.amount) / 100).toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <a
                              href={link.payment_link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                            >
                              View Link <LinkIcon className="h-3 w-3 ml-1" />
                            </a>
                            {link.status === 'pending' && (
                              <button
                                onClick={() => handleDeletePaymentLink(link.payment_link_id)}
                                disabled={deletingLinkId === link.payment_link_id}
                                className="text-xs text-red-600 hover:text-red-800 flex items-center disabled:opacity-50"
                                title="Delete payment link"
                              >
                                <Trash2Icon className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {getRemainingBalance() > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <CreditCardIcon className="h-5 w-5 mr-2" style={{ color: theme.colors.primary[600] }} />
                    Create Payment Link
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Description
                      </label>
                      <input
                        type="text"
                        value={paymentLinkDescription}
                        onChange={(e) => setPaymentLinkDescription(e.target.value)}
                        placeholder="e.g., Remaining balance for bounce house rental"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
                      />
                    </div>
                    <button
                      onClick={handleCreatePaymentLink}
                      disabled={creatingLink || !paymentLinkDescription.trim()}
                      className="w-full px-4 py-2 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: theme.colors.primary[600] }}
                    >
                      {creatingLink ? 'Creating...' : `Create Payment Link ($${getRemainingBalance().toFixed(2)})`}
                    </button>
                    {paymentLink && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-green-800 mb-1">Payment Link Created!</p>
                            <p className="text-xs text-green-700 break-all font-mono">{paymentLink}</p>
                          </div>
                          <button
                            onClick={() => copyToClipboard(paymentLink)}
                            className="ml-2 text-green-600 hover:text-green-800"
                            title="Copy to clipboard"
                          >
                            <LinkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailModal;
